import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import EventTemplateForm, { IEventTemplateFormData } from '../../components/forms/EventTemplateForm';
import CollapsibleWidget from '../../components/CollapsibleWidget';
import TemplateEventsWidget from '../../components/TemplateEventsWidget';
import TemplateChangePreview from '../../components/TemplateChangePreview';
import TemplateUpdateModal, { UpdateOption } from '../../components/TemplateUpdateModal';
import RoleAccessWidget from '../../components/RoleAccessWidget';
import { translateError } from '../../utils/errorMessages';
import { rosterPeopleFrom } from '../../components/AssignmentDrawer';

interface IEventTemplate {
  _id: string;
  name: string;
  description: string;
  location?: string | null;
  firstOccurrence: string;
  endsOn?: string | null;
  startTime: string;
  endTime: string;
  recurringPattern: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  defaultAttendance: boolean | null;
  members: Array<{ _id: number; name: string } | string>;
  groups: Array<{ _id: number; name: string } | string>;
  coveredByMembershipIds?: number[];
  /** Admin users notified about this template's events (migration 0047). */
  followers?: Array<{ _id: number; name: string } | number>;
  isActive: boolean;
  // Backend serializes the responsible person as a numeric `responsibleId`
  // (via `...template`), NOT a `responsible` string.
  responsibleId?: number | null;
  activityId?: number | null;
  courseId?: number | null;
}

// Order-independent normalization of a single field value, used for generic
// change detection. Array fields (m2m selections) are sorted so selection order
// doesn't register as a change; scalars are stringified and empty/null/undefined
// are unified, so '' === null === undefined and number 1 === string "1" don't
// produce spurious diffs across the initialData → buildPayload round-trip.
const normalizeFieldValue = (val: unknown): string =>
  Array.isArray(val)
    ? JSON.stringify([...val].map(String).sort())
    : val === null || val === undefined || val === '' ? '' : String(val);

// Single source of truth for "what changed": the list of field names whose
// normalized value differs between the current form payload and the baseline
// seed. Drives both `hasChanges` (modal gating) and the change preview, so the
// two can never disagree. Adding a new form field needs no edit here.
const computeChangedFields = (
  current: Partial<IEventTemplateFormData>,
  baseline: Partial<IEventTemplateFormData>,
): string[] => {
  const keys = new Set([...Object.keys(current), ...Object.keys(baseline)]);
  const changed: string[] = [];
  for (const key of keys) {
    if (normalizeFieldValue((current as Record<string, any>)[key]) !== normalizeFieldValue((baseline as Record<string, any>)[key])) {
      changed.push(key);
    }
  }
  return changed;
};

const EventTemplateEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Original template data (for comparison)
  const [eventTemplate, setEventTemplate] = useState<IEventTemplate | null>(null);

  // Current form values (tracked via onValuesChange)
  const [currentValues, setCurrentValues] = useState<IEventTemplateFormData | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'form' | 'events'>('form');

  useEffect(() => {
    const fetchEventTemplate = async () => {
      try {
        setLoading(true);
        const { data } = await api.eventTemplates.get(id!);
        const template = data as IEventTemplate;
        setEventTemplate(template);
        setLoading(false);
      } catch (err: any) {
        setError(translateError(err, t));
        setLoading(false);
        if (err.response && err.response.status === 404) {
          navigate('/admin/event-templates');
        }
      }
    };

    if (id) {
      fetchEventTemplate();
    }
  }, [id, navigate]);

  // Convert template to form initial data
  const initialFormData = useMemo((): Partial<IEventTemplateFormData> | undefined => {
    if (!eventTemplate) return undefined;

    const dateObj = new Date(eventTemplate.firstOccurrence);
    return {
      name: eventTemplate.name,
      description: eventTemplate.description,
      location: eventTemplate.location ?? '',
      firstOccurrence: dateObj.toISOString().split('T')[0],
      endsOn: eventTemplate.endsOn ? new Date(eventTemplate.endsOn).toISOString().split('T')[0] : '',
      startTime: eventTemplate.startTime,
      endTime: eventTemplate.endTime,
      recurringPattern: eventTemplate.recurringPattern,
      defaultAttendance: eventTemplate.defaultAttendance,
      members: eventTemplate.members.map((m: any) => typeof m === 'object' ? m._id : m),
      groups: eventTemplate.groups.map((g: any) => typeof g === 'object' ? g._id : g),
      coveredByMembershipIds: eventTemplate.coveredByMembershipIds || [],
      followers: (eventTemplate.followers || []).map((f: any) => typeof f === 'object' ? f._id : f),
      responsible: eventTemplate.responsibleId != null ? String(eventTemplate.responsibleId) : null,
      activityId: eventTemplate.activityId ? String(eventTemplate.activityId) : null,
      courseId: eventTemplate.courseId ?? null,
      isActive: eventTemplate.isActive,
    };
  }, [eventTemplate]);

  const rosterPeople = useMemo(() => rosterPeopleFrom(eventTemplate?.members), [eventTemplate]);

  // Unified change detection: one list of changed field names, derived from the
  // current form payload vs the initial seed. `hasChanges` (modal gating) and the
  // TemplateChangePreview are both driven from this, so they can never disagree.
  const changedFields = useMemo(
    () => (currentValues && initialFormData ? computeChangedFields(currentValues, initialFormData) : []),
    [currentValues, initialFormData],
  );

  const hasChanges = changedFields.length > 0;

  // Check if schedule fields changed
  const scheduleChanged = useMemo(() => {
    if (!eventTemplate || !currentValues) return false;

    const originalDate = new Date(eventTemplate.firstOccurrence).toISOString().split('T')[0];

    if (currentValues.firstOccurrence !== originalDate) return true;
    if (currentValues.startTime !== eventTemplate.startTime) return true;
    if (currentValues.endTime !== eventTemplate.endTime) return true;
    if (currentValues.recurringPattern !== eventTemplate.recurringPattern) return true;

    return false;
  }, [eventTemplate, currentValues]);

  const handleSubmit = async (data: IEventTemplateFormData) => {
    // If there are changes, show the update modal
    if (hasChanges) {
      setShowUpdateModal(true);
    } else {
      // No changes, just navigate back
      navigate('/admin/event-templates');
    }
  };

  const handleUpdateConfirm = useCallback(async (option: UpdateOption, selectedEventIds?: string[]) => {
    if (!currentValues) return;

    setSubmitting(true);
    setError(null);

    try {
      const updatePayload: Record<string, any> = {
        name: currentValues.name,
        description: currentValues.description,
        location: currentValues.location ?? null,
        firstOccurrence: new Date(currentValues.firstOccurrence + 'T12:00:00').toISOString(),
        endsOn: currentValues.endsOn ? currentValues.endsOn : null,
        startTime: currentValues.startTime,
        endTime: currentValues.endTime,
        recurringPattern: currentValues.recurringPattern,
        defaultAttendance: currentValues.defaultAttendance,
        members: currentValues.members,
        groups: currentValues.groups,
        coveredByMembershipIds: currentValues.coveredByMembershipIds,
        followers: currentValues.followers,
        isActive: currentValues.isActive,
        responsible: currentValues.responsible,
        activityId: currentValues.activityId ? Number(currentValues.activityId) : null,
        courseId: currentValues.courseId,
        updateOption: option,
      };

      if (option === 'selected' && selectedEventIds) {
        updatePayload.selectedEventIds = selectedEventIds;
      }

      const response = await api.eventTemplates.update(id!, updatePayload);

      const r = response.data as { eventsDeleted: number; eventsGenerated: number; eventsUpdated: number; eventsSkipped: number };
      setSubmitting(false);
      setShowUpdateModal(false);
      const parts: string[] = [];
      if (r.eventsDeleted > 0) parts.push(t('templates.updateSummary.deleted', { count: r.eventsDeleted }));
      if (r.eventsGenerated > 0) parts.push(t('templates.updateSummary.generated', { count: r.eventsGenerated }));
      if (r.eventsUpdated > 0) parts.push(t('templates.updateSummary.updated', { count: r.eventsUpdated }));
      if (r.eventsSkipped > 0) parts.push(t('templates.updateSummary.skipped', { count: r.eventsSkipped }));
      const summary = parts.length > 0
        ? `${t('templates.updateSummary.prefix')} ${parts.join(', ')}`
        : `${t('templates.updateSummary.prefix')} ${t('templates.updateSummary.none')}`;
      addToast(summary, 'success');
      navigate('/admin/event-templates');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setSubmitting(false);
    }
  }, [id, currentValues, addToast, navigate, t]);

  if (loading && !eventTemplate) return <p>{t('templates.loadingTemplate')}</p>;
  if (error && !eventTemplate) return <p className="error-message">{t('common.error')}: {error}</p>;
  if (!eventTemplate) return <p>{t('templates.templateNotFound')}</p>;

  return (
    <div className="admin-page-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>{t('templates.editTitle')}</h2>
      </div>

      {/* Mobile Tab Switcher */}
      <div className="tab-switcher mobile-only">
        <button
          className={activeTab === 'form' ? 'active' : ''}
          onClick={() => setActiveTab('form')}
        >
          {t('eventDetails.tabEdit', 'Edit')}
        </button>
        <button
          className={activeTab === 'events' ? 'active' : ''}
          onClick={() => setActiveTab('events')}
        >
          {t('templateEvents.title', 'Events')}
        </button>
      </div>

      <div className="edit-page-layout">
        <div className={`edit-page-form ${activeTab === 'form' ? 'active' : ''}`}>
          {error && <p className="error-message">{t('common.error')}: {error}</p>}

          <EventTemplateForm
            initialData={initialFormData}
            onSubmit={handleSubmit}
            loading={submitting}
            submitButtonText={t('templates.updateButton')}
            showIsActive={true}
            onValuesChange={setCurrentValues}
            rosterPeople={rosterPeople}
          >
            {/* Change Preview - driven by the same unified changedFields as hasChanges */}
            {hasChanges && currentValues && (
              <TemplateChangePreview
                templateId={id!}
                changedFields={changedFields}
                currentValues={{
                  ...currentValues,
                  responsible: currentValues.responsible || undefined,
                }}
              />
            )}
          </EventTemplateForm>
          <Link to="/admin/event-templates" className="form-link">{t('templates.goBack')}</Link>
        </div>

        {/* Sidebar with events widget */}
        <div className={`edit-page-widgets ${activeTab === 'events' ? 'active' : ''}`}>
          {id && (
            <>
              <CollapsibleWidget
                title={t('templateEvents.title', 'Generated Events')}
                defaultExpanded={true}
                storageKey={`template-${id}-events`}
              >
                <TemplateEventsWidget templateId={id} />
              </CollapsibleWidget>
              <CollapsibleWidget
                title={t('roles.entityAccess')}
                defaultExpanded={false}
                storageKey={`template-${id}-role-access`}
              >
                <RoleAccessWidget entityType="eventTemplates" entityId={id} />
              </CollapsibleWidget>
            </>
          )}
        </div>
      </div>

      {/* Update Modal */}
      {currentValues && (
        <TemplateUpdateModal
          isOpen={showUpdateModal}
          onClose={() => setShowUpdateModal(false)}
          onConfirm={handleUpdateConfirm}
          templateId={id!}
          changes={currentValues}
          scheduleChanged={scheduleChanged}
          loading={submitting}
        />
      )}
    </div>
  );
};

export default EventTemplateEditPage;
